import DOMPurify from 'dompurify';

class CryptoDatabaseService {
  constructor() {
    this.dbKey = 'crypto_submissions_db';
    this.backupKey = 'crypto_submissions_backup';
    this.maxFileSize = 5 * 1024 * 1024; // 5MB
    this.allowedFileTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'application/pdf'];
    this.maxSubmissions = 10000; // Prevent memory issues
  }

  // Input validation and sanitization
  validateAndSanitizeSubmission(data) {
    const errors = [];

    // Required fields validation
    if (!data.userEmail || typeof data.userEmail !== 'string') {
      errors.push('Valid email is required');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.userEmail)) {
      errors.push('Invalid email format');
    }

    if (!data.amount || isNaN(parseFloat(data.amount)) || parseFloat(data.amount) <= 0) {
      errors.push('Valid amount is required');
    }

    if (!data.cryptoType || typeof data.cryptoType !== 'string') {
      errors.push('Crypto type is required');
    }

    // Length validations
    if (data.userEmail && data.userEmail.length > 100) {
      errors.push('Email too long');
    }

    if (data.walletAddress && data.walletAddress.length > 200) {
      errors.push('Wallet address too long');
    }

    if (data.transactionId && data.transactionId.length > 200) {
      errors.push('Transaction ID too long');
    }

    if (errors.length > 0) {
      throw new Error(errors.join(', '));
    }

    // Sanitize all string inputs
    const sanitized = {
      id: Date.now() + Math.random().toString(36).substr(2, 9),
      userEmail: DOMPurify.sanitize(data.userEmail.trim().toLowerCase()),
      amount: parseFloat(data.amount),
      cryptoType: DOMPurify.sanitize(data.cryptoType.trim().toUpperCase()),
      walletAddress: data.walletAddress ? DOMPurify.sanitize(data.walletAddress.trim()) : '',
      transactionId: data.transactionId ? DOMPurify.sanitize(data.transactionId.trim()) : '',
      account: data.account ? DOMPurify.sanitize(data.account.trim()) : '',
      appEnv: data.appEnv ? DOMPurify.sanitize(data.appEnv.trim()) : 'development',
      submittedTime: data.submittedTime || new Date().toISOString(),
      expiredTime: data.expiredTime || '',
      status: 'pending',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      ipAddress: this.getClientIP(),
      userAgent: navigator.userAgent.substring(0, 200) // Limit length
    };

    return sanitized;
  }

  // Validate file upload
  validateFile(file) {
    if (!file) {
      throw new Error('File is required');
    }

    if (file.size > this.maxFileSize) {
      throw new Error('File size too large (max 5MB)');
    }

    if (!this.allowedFileTypes.includes(file.type)) {
      throw new Error('Invalid file type. Only images and PDF allowed');
    }

    // Check file name
    if (file.name.length > 100) {
      throw new Error('Filename too long');
    }

    // Basic file content validation
    if (file.type.startsWith('image/')) {
      return this.validateImageFile(file);
    }

    return true;
  }

  // Validate image file
  validateImageFile(file) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        // Check reasonable image dimensions
        if (img.width > 5000 || img.height > 5000) {
          reject(new Error('Image dimensions too large'));
        } else {
          resolve(true);
        }
      };
      img.onerror = () => {
        reject(new Error('Invalid image file'));
      };
      img.src = URL.createObjectURL(file);
    });
  }

  // Get client IP (limited in browser)
  getClientIP() {
    // In a real app, this would be handled server-side
    return 'client';
  }

  // Load submissions from localStorage
  loadSubmissions() {
    try {
      const data = localStorage.getItem(this.dbKey);
      if (!data) return [];

      const submissions = JSON.parse(data);

      // Validate data structure
      if (!Array.isArray(submissions)) {
        throw new Error('Invalid data format');
      }

      // Limit number of submissions
      if (submissions.length > this.maxSubmissions) {
        submissions.splice(this.maxSubmissions);
      }

      return submissions;
    } catch (error) {
      console.error('Error loading submissions:', error);

      // Try to load backup
      try {
        const backup = localStorage.getItem(this.backupKey);
        if (backup) {
          const backupData = JSON.parse(backup);
          if (Array.isArray(backupData)) {
            console.log('Loaded from backup');
            return backupData;
          }
        }
      } catch (backupError) {
        console.error('Backup also corrupted:', backupError);
      }

      return [];
    }
  }

  // Save submissions to localStorage
  saveSubmissions(submissions) {
    try {
      // Validate submissions array
      if (!Array.isArray(submissions)) {
        throw new Error('Submissions must be an array');
      }

      // Create backup before saving
      const currentData = localStorage.getItem(this.dbKey);
      if (currentData) {
        localStorage.setItem(this.backupKey, currentData);
      }

      // Save new data
      localStorage.setItem(this.dbKey, JSON.stringify(submissions));
      return true;
    } catch (error) {
      console.error('Error saving submissions:', error);
      throw new Error('Failed to save submission');
    }
  }

  // Add new submission
  async addSubmission(data, file = null) {
    try {
      // Validate and sanitize input
      const sanitized = this.validateAndSanitizeSubmission(data);

      // Validate file if provided
      if (file) {
        await this.validateFile(file);
        sanitized.filename = this.generateSecureFilename(file.name);
        sanitized.originalName = file.name;
        sanitized.fileSize = file.size;
        sanitized.fileType = file.type;

        // Store file as base64 (in production, use proper file storage)
        sanitized.fileData = await this.fileToBase64(file);
      }

      // Load existing submissions
      const submissions = this.loadSubmissions();

      // Check for duplicates (same user, amount, and recent timestamp)
      const recentSubmission = submissions.find(sub =>
        sub.userEmail === sanitized.userEmail &&
        sub.amount === sanitized.amount &&
        (Date.now() - new Date(sub.createdAt).getTime()) < 5 * 60 * 1000 // 5 minutes
      );

      if (recentSubmission) {
        throw new Error('Duplicate submission detected');
      }

      // Add new submission
      submissions.unshift(sanitized); // Add to beginning

      // Limit total submissions
      if (submissions.length > this.maxSubmissions) {
        submissions.pop(); // Remove oldest
      }

      // Save to storage
      this.saveSubmissions(submissions);

      return sanitized.id;
    } catch (error) {
      console.error('Error adding submission:', error);
      throw error;
    }
  }

  // Convert file to base64
  fileToBase64(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsDataURL(file);
    });
  }

  // Generate secure filename
  generateSecureFilename(originalName) {
    const timestamp = Date.now();
    const random = Math.random().toString(36).substr(2, 9);
    const extension = originalName.split('.').pop().toLowerCase();

    // Sanitize extension
    const safeExtension = extension.replace(/[^a-z0-9]/g, '').substring(0, 10);

    return `${timestamp}_${random}.${safeExtension}`;
  }

  // Get all submissions (admin only)
  getAllSubmissions() {
    return this.loadSubmissions();
  }

  // Update submission status
  updateSubmissionStatus(submissionId, status) {
    try {
      const validStatuses = ['pending', 'approved', 'rejected'];
      if (!validStatuses.includes(status)) {
        throw new Error('Invalid status');
      }

      const submissions = this.loadSubmissions();
      const submission = submissions.find(sub => sub.id === submissionId);

      if (!submission) {
        throw new Error('Submission not found');
      }

      submission.status = status;
      submission.updatedAt = new Date().toISOString();

      this.saveSubmissions(submissions);
      return true;
    } catch (error) {
      console.error('Error updating status:', error);
      throw error;
    }
  }

  // Get submission by ID
  getSubmissionById(submissionId) {
    const submissions = this.loadSubmissions();
    return submissions.find(sub => sub.id === submissionId);
  }

  // Clear all data (admin only, with confirmation)
  clearAllData(confirmationCode) {
    if (confirmationCode !== 'CLEAR_ALL_CRYPTO_DATA_2024') {
      throw new Error('Invalid confirmation code');
    }

    localStorage.removeItem(this.dbKey);
    localStorage.removeItem(this.backupKey);
    return true;
  }

  // Export data for backup
  exportData() {
    const submissions = this.loadSubmissions();
    return {
      exportDate: new Date().toISOString(),
      submissionCount: submissions.length,
      data: submissions
    };
  }
}

export default new CryptoDatabaseService();